import {dispatchApi} from 'common/helpers/chunkedUpload'
import {requestAllocationServer} from 'setup/redux/Actions/Onboarding/teacherAllocationServer'

import type {
  AllocationEditResult,
  AllocationPrefillAnswer,
  AllocationPrefillResult,
  AllocationProposalGrid,
  AllocationRemovalApplied,
  AllocationRemovalPreview,
  AllocationRemovalRequest,
  AllocationServerError,
  AllocationServerGrid,
  AllocationServerSummary,
  AllocationUndoResult,
  VersiServerApply,
  VersiServerProposal,
  VersiServerStart,
  VersiServerTurn,
} from '../types/allocationServer'

type Dispatch = (action: any) => unknown

/** Branch / Level as the API takes them: an id, or nothing for All. */
export type AllocationScopeQuery = {branchId?: number | null; levelId?: number | null}

export type AllocationGridFilter = 'all' | 'assigned' | 'unassigned'

/** Any failure as `{statusCode, code, message, result}`; a network error has statusCode 0. */
export function toAllocationError(error: any): AllocationServerError {
  return {
    statusCode: Number(error?.statusCode) || 0,
    code: typeof error?.code === 'string' ? error.code : null,
    message: typeof error?.message === 'string' ? error.message : '',
    result: error?.result ?? null,
  }
}

/**
 * The allocation routes exist on the AI servers only; everywhere else they answer a bare
 * `404 Not found` (no `code`), which is how the portal tells it should use the client flow.
 */
export const isAllocationUnavailable = (error: AllocationServerError) =>
  error.statusCode === 404 && !error.code

const call = <T>(
  dispatch: Dispatch,
  request: Parameters<typeof requestAllocationServer>[0]
): Promise<T> =>
  dispatchApi(dispatch as any, (ok, fail) => requestAllocationServer(request, ok, fail)).then(
    (res: any) => res?.result as T,
    (error) => Promise.reject(toAllocationError(error))
  )

const scopeQuery = (scope?: AllocationScopeQuery) => ({
  branchId: scope?.branchId ?? undefined,
  levelId: scope?.levelId ?? undefined,
})

/** Promise wrappers over the server-side Teacher Allocation endpoints (handoff §2–§7). */
export const allocationServerApi = {
  prefill: (dispatch: Dispatch, branchId?: number | 'all') =>
    call<AllocationPrefillResult>(dispatch, {
      path: '/prefill',
      method: 'POST',
      body: branchId === undefined ? {} : {branchId},
    }),

  /** The prefill's unclear lines answered (`questions`): which classes, by teacher + subject. */
  answerPrefill: (dispatch: Dispatch, answers: AllocationPrefillAnswer[]) =>
    call<AllocationPrefillResult>(dispatch, {
      path: '/prefill',
      method: 'POST',
      body: {
        answers: answers.map(({teacher, employeeId, subject, classIds}) => ({
          teacher,
          ...(employeeId ? {employeeId} : {}),
          subject,
          classIds,
        })),
      },
    }),

  summary: (dispatch: Dispatch, scope?: AllocationScopeQuery) =>
    call<AllocationServerSummary>(dispatch, {path: '/summary', query: scopeQuery(scope)}),

  grid: (dispatch: Dispatch, scope?: AllocationScopeQuery, filter: AllocationGridFilter = 'all') =>
    call<AllocationServerGrid>(dispatch, {path: '/grid', query: {...scopeQuery(scope), filter}}),

  proposalGrid: (dispatch: Dispatch, proposalId: string, scope?: AllocationScopeQuery) =>
    call<AllocationProposalGrid>(dispatch, {
      path: `/assistant/proposal/${proposalId}/grid`,
      query: {...scopeQuery(scope), filter: 'all'},
    }),

  setTeacher: (dispatch: Dispatch, subjectId: number, teacherId: number | null) =>
    call<AllocationEditResult>(dispatch, {
      path: `/cell/${subjectId}/teacher`,
      method: 'PATCH',
      body: {teacherId},
    }),

  setSettings: (
    dispatch: Dispatch,
    subjectId: number,
    body: {weeklyPeriods?: number; dailyPeriods?: number; consecutivePeriods?: number}
  ) =>
    call<AllocationEditResult>(dispatch, {
      path: `/cell/${subjectId}/settings`,
      method: 'PATCH',
      body,
    }),

  markNotTaught: (dispatch: Dispatch, subjectId: number) =>
    call<AllocationEditResult>(dispatch, {path: `/cell/${subjectId}/not-taught`, method: 'POST'}),

  restore: (dispatch: Dispatch, subjectId: number) =>
    call<AllocationEditResult>(dispatch, {path: `/cell/${subjectId}/restore`, method: 'POST'}),

  undo: (dispatch: Dispatch, batchId: string) =>
    call<AllocationUndoResult>(dispatch, {path: `/batch/${batchId}/undo`, method: 'POST'}),

  previewRemoval: (dispatch: Dispatch, body: AllocationRemovalRequest) =>
    call<AllocationRemovalPreview>(dispatch, {path: '/removal/preview', method: 'POST', body}),

  applyRemoval: (dispatch: Dispatch, body: AllocationRemovalRequest & {expectedCount: number}) =>
    call<AllocationRemovalApplied>(dispatch, {path: '/removal/apply', method: 'POST', body}),

  assistantStart: (dispatch: Dispatch, scope?: AllocationScopeQuery) =>
    call<VersiServerStart>(dispatch, {path: '/assistant/start', query: scopeQuery(scope)}),

  /**
   * One Versi turn (5–25 s). After 45 s the server keeps the connection alive with spaces and
   * answers HTTP 200 whatever happened: the saga already decides by the body's `status`, and the
   * body's `statusCode` / `code` carry the real outcome.
   */
  assistantMessage: (
    dispatch: Dispatch,
    body: {text: string; choice?: string; branchId?: number | null; levelId?: number | null}
  ) =>
    call<VersiServerTurn>(dispatch, {
      path: '/assistant/message',
      method: 'POST',
      body: {
        text: body.text,
        ...(body.choice ? {choice: body.choice} : {}),
        ...(body.branchId != null ? {branchId: body.branchId} : {}),
        ...(body.levelId != null ? {levelId: body.levelId} : {}),
      },
    }),

  applyProposal: (dispatch: Dispatch, proposalId: string, confirmRemoval: boolean) =>
    call<VersiServerApply>(dispatch, {
      path: `/assistant/proposal/${proposalId}/apply`,
      method: 'POST',
      body: confirmRemoval ? {confirmRemoval: true} : {},
    }),

  discardProposal: (dispatch: Dispatch, proposalId: string) =>
    call<{proposal: VersiServerProposal}>(dispatch, {
      path: `/assistant/proposal/${proposalId}/discard`,
      method: 'POST',
    }),

  /** Answers like `assistantStart` (empty conversation), with examples for the scope sent. */
  resetAssistant: (dispatch: Dispatch, scope?: AllocationScopeQuery) =>
    call<VersiServerStart>(dispatch, {
      path: '/assistant/reset',
      method: 'POST',
      body: {
        ...(scope?.branchId != null ? {branchId: scope.branchId} : {}),
        ...(scope?.levelId != null ? {levelId: scope.levelId} : {}),
      },
      query: scopeQuery(scope),
    }),
}
